import { ArrowUpDown, Copy, CreditCard, Star, ExternalLink, Gift, Lock, Minus, PackagePlus, Pause, Play, Plus, PlugZap, Unplug, Wrench } from 'lucide-react';
import { DownloadRecords } from './DownloadRecords';
import { useCallback, useEffect, useState } from 'react';
import { api, setBillingPageShown } from '../lib/api';
import type { BillingAdmin, BillingInfo, Parent, PlatformSettings } from '../lib/types';
import { Fold } from '../ui/Collapsible';
import { Logo } from '../ui/Logo';
import { ExportData } from './AccountSecurity';
import { useParent } from './ParentContext';
import { YourPlan } from './Plans';

/** Billing with Stripe (#540): paying for the family's plan, managing it, and the page a locked family sees. */

const money = (cents: number) => (cents % 100 ? `$${(cents / 100).toFixed(2)}` : `$${cents / 100}`);
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** Where the subscription stands, in words. */
function statusLine(b: BillingInfo): string {
  if (b.pausedUntil) return `Billing is paused until ${day(b.pausedUntil)}. Your records stay open; children’s computers aren’t managed until then.`;
  const per = b.interval === 'year' ? 'yearly' : b.interval === 'month' ? 'monthly' : '';
  switch (b.status) {
    case 'active':
      return b.cancelAtPeriodEnd ? `Cancelled. Your plan works until ${day(b.periodEnd)}.` : `Subscribed${per ? ` (${per})` : ''}. Renews ${day(b.periodEnd)}.`;
    case 'trialing':
      return `Subscribed${per ? ` (${per})` : ''}. Your free trial ends ${day(b.periodEnd)}, then billing starts.`;
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
      return b.locked ? 'A payment didn’t go through.' : `A payment didn’t go through. Update your card by ${day(b.graceEndsAt)} to keep everything working.`;
    case 'canceled':
      return 'Your subscription has ended.';
    default:
      return b.needsPayment ? 'Your plan needs a subscription.' : 'No subscription needed right now.';
  }
}

/** Opens Stripe's Checkout or customer portal. */
async function go(path: string, body?: object) {
  const r = await api.post<{ url: string }>(path, body);
  window.location.assign(r.url);
}

/**
 * Pick a plan and monthly or yearly. Not subscribed yet: pay on Stripe's page. Already subscribed ("change"): switch
 * plan or period (#542), and Stripe works out the difference on the next invoice.
 */
type PickerPlan = BillingInfo['plans'][number];
/**
 * The plan a family of `kids` gets when it picks `base` (#633): the base plan with extra-children places, or its bigger
 * (Large Family) version once the places run out; null when nothing covers that many.
 */
function sized(plans: PickerPlan[], base: PickerPlan, kids: number): { plan: PickerPlan; extra: number } | null {
  if (base.maxChildren == null || kids <= base.maxChildren) return { plan: base, extra: 0 };
  const a = base.childAddon;
  const need = a ? Math.ceil((kids - base.maxChildren) / Math.max(1, a.amount)) : Infinity;
  if (a && (!a.maxQuantity || need <= a.maxQuantity)) return { plan: base, extra: need };
  const larger = plans.find((p) => p.largerOf === base.id && (p.maxChildren == null || kids <= p.maxChildren));
  return larger ? { plan: larger, extra: 0 } : null;
}

function PlanPicker({ info, mode = 'subscribe', onChanged, onChosen }: { info: BillingInfo; mode?: 'subscribe' | 'change' | 'choose'; onChanged?: () => void; onChosen?: (p: Parent) => void }) {
  const { toast, children } = useParent();
  const [interval, setPeriod] = useState<'month' | 'year'>(info.interval === 'year' ? 'year' : 'month');
  // How many children the family is paying for (#633): starts at how many it has.
  const [kids, setKids] = useState(Math.max(1, children.length));
  const [busy, setBusy] = useState('');
  // Founders (#585): their lifetime discount, shown on every price.
  const pct = info.founderPercent ?? 0;
  const founder = (cents: number) => Math.round(cents * (1 - pct / 100));
  if (!info.plans.length) return <p className="muted small">There’s no plan to subscribe to yet. Contact us and we’ll sort it out.</p>;
  const discount = Math.max(0, ...info.plans.map((p) => p.yearlyDiscountPct));
  const placesNow = (p: PickerPlan) => (p.childAddon ? (info.addons?.find((a) => a.id === p.childAddon!.id)?.quantity ?? 0) : 0);
  const isCurrent = (p: PickerPlan, extra = 0) => mode === 'change' && p.current && interval === info.interval && extra === placesNow(p);
  const priceOf = (s: { plan: PickerPlan; extra: number }) =>
    (interval === 'month' ? s.plan.monthlyCents : s.plan.yearlyCents) + s.extra * (s.plan.childAddon ? (interval === 'month' ? s.plan.childAddon.monthlyCents : s.plan.childAddon.yearlyCents) : 0);
  const bases = info.plans.filter((p) => !p.largerOf);
  const sizedPlans = info.plans.some((p) => p.childAddon || p.largerOf || p.maxChildren != null);
  async function pick(p: PickerPlan, extra = 0) {
    if (mode === 'change') {
      const price = `${money(priceOf({ plan: p, extra }))}/${interval === 'month' ? 'month' : 'year'}`;
      // Same rule as the server (#544): costs more a month, or monthly → yearly, is charged now.
      const now = info.plans.find((x) => x.current);
      const perMonth = (x: BillingInfo['plans'][number], i: string) => (i === 'year' ? x.yearlyCents / 12 : x.monthlyCents);
      const upgrade = !now || perMonth(p, interval) > perMonth(now, info.interval) || (info.interval === 'month' && interval === 'year');
      const when = upgrade
        ? 'It starts now, and your card is charged today for the difference for the rest of this billing period.'
        : 'It starts now, and the unused part of your current plan is credited to your next invoice.';
      if (!confirm(`Switch to ${p.name} at ${price}? ${when}`)) return;
    }
    setBusy(p.id);
    try {
      const children = sizedPlans ? kids : undefined;
      // A new family picks its plan (#635): the trial starts now, or Checkout opens for a plan without one.
      if (mode === 'choose') {
        const r = await api.post<{ plan: string; url: string }>('/api/billing/choose-plan', { planId: p.id, interval, children });
        if (r.url) {
          window.location.href = r.url;
          return;
        }
        toast({ text: `You’re on ${r.plan}. Welcome!`, tone: 'good' });
        onChosen?.(await api.get<Parent>('/api/me'));
        return;
      }
      if (mode === 'subscribe') return await go('/api/billing/checkout', { planId: p.id, interval, children });
      const r = await api.post<{ plan: string; chargedNow?: boolean }>('/api/billing/change-plan', { planId: p.id, interval, children });
      toast({ text: `You’re now on ${r.plan}.${r.chargedNow ? ' The difference was charged to your card.' : ' The credit shows on your next invoice.'}`, tone: 'good' });
      onChanged?.();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
    setBusy('');
  }
  return (
    <div className="stack billing-picker">
      <div className="chips" role="group" aria-label="Billing period">
        <button className={`chip ${interval === 'month' ? 'active' : ''}`} aria-pressed={interval === 'month'} onClick={() => setPeriod('month')}>
          Monthly
        </button>
        <button className={`chip ${interval === 'year' ? 'active' : ''}`} aria-pressed={interval === 'year'} onClick={() => setPeriod('year')}>
          Yearly{discount ? <span className="tag good">save up to {discount}%</span> : null}
        </button>
      </div>
      {sizedPlans && (
        <div className="kids-stepper" role="group" aria-label="How many children">
          <span>How many children?</span>
          <button type="button" className="icon-btn" aria-label="Fewer children" onClick={() => setKids((k) => Math.max(1, k - 1))} disabled={kids <= 1}>
            <Minus size={16} />
          </button>
          <strong aria-live="polite">{kids}</strong>
          <button type="button" className="icon-btn" aria-label="More children" onClick={() => setKids((k) => Math.min(50, k + 1))}>
            <Plus size={16} />
          </button>
          {children.length > 0 && <span className="muted small">You have {children.length} now</span>}
        </div>
      )}
      <div className="billing-plans">
        {bases.map((b) => {
          const s = sized(info.plans, b, kids);
          if (!s) {
            const top = info.plans.filter((p) => p.id === b.id || p.largerOf === b.id).reduce((n, p) => Math.max(n, p.maxChildren ?? 0), 0);
            return (
              <div key={b.id} className="billing-plan">
                <div>
                  <strong>{b.name}</strong>
                  <p className="muted small">Covers up to {top} children. Contact us about a bigger family.</p>
                </div>
              </div>
            );
          }
          const p = s.plan;
          const unit = interval === 'month' ? p.childAddon?.monthlyCents : p.childAddon?.yearlyCents;
          // A Large Family plan starts where the regular plan (with its most extra places) stops.
          const regular = p.largerOf ? info.plans.find((x) => x.id === p.largerOf) : undefined;
          const from = regular ? (regular.maxChildren ?? 0) + (regular.childAddon?.maxQuantity ? regular.childAddon.maxQuantity * regular.childAddon.amount : 0) + 1 : 0;
          const math = p.largerOf
            ? `Covers ${from}–${p.maxChildren ?? '∞'} children`
            : s.extra && p.maxChildren != null && unit !== undefined
              ? `${money(interval === 'month' ? p.monthlyCents : p.yearlyCents)} for ${p.maxChildren} + ${s.extra} × ${money(unit)}`
              : p.maxChildren != null
                ? `Up to ${p.maxChildren} child${p.maxChildren === 1 ? '' : 'ren'}`
                : 'Any number of children';
          return (
            <div key={b.id} className={`billing-plan ${p.current ? 'current' : ''}`}>
              <div>
                <strong>{p.name}</strong> {p.current && <span className="tag">Your plan</span>}
                {p.description && <p className="muted small">{p.description}</p>}
                <p className="muted small">
                  {math}
                  {p.maxAdults != null && ` · up to ${p.maxAdults} adult${p.maxAdults === 1 ? '' : 's'}`}
                </p>
              </div>
              <div className="billing-price">
                <strong>{money(founder(priceOf(s)))}</strong>
                <span className="muted small">{interval === 'month' ? '/month' : '/year'}</span>
                {pct > 0 && (
                  <div className="muted small">
                    <s>{money(priceOf(s))}</s> for everyone else
                  </div>
                )}
              </div>
              <button className={`btn ${isCurrent(p, s.extra) ? '' : 'primary'}`} onClick={() => pick(p, s.extra)} disabled={!!busy || isCurrent(p, s.extra)}>
                {busy === p.id
                  ? mode === 'change' ? 'Switching…' : 'Opening…'
                  : isCurrent(p, s.extra)
                    ? 'Current plan'
                    : mode === 'choose'
                      ? p.trialDays ? `Start ${p.trialDays} days free` : 'Subscribe'
                      : mode === 'subscribe' ? 'Subscribe' : 'Switch to this plan'}
              </button>
            </div>
          );
        })}
      </div>
      <p className="muted small">
        {mode === 'subscribe' ? (
          <>
            You’ll pay on Stripe’s secure page{info.taxEnabled ? '; sales tax is added where it applies' : ''}. ClassGuardian never sees your card. Your
            plan renews automatically until you cancel, and you can ask for a refund within 14 days of a charge (
            <a href="/terms" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
            ).
          </>
        ) : mode === 'choose' ? (
          'You can switch plans any time in Settings → Plan & billing. A plan without free days opens Stripe’s secure page to pay.'
        ) : (
          'A bigger plan starts now and the difference for the rest of this period is charged right away; a smaller one starts now and leaves a credit on your next invoice.'
        )}
      </p>
    </div>
  );
}

function useBilling() {
  const { toast } = useParent();
  const [info, setInfo] = useState<BillingInfo | null>(null);
  const load = useCallback(() => api.get<BillingInfo>('/api/billing').then(setInfo).catch((e) => toast({ text: (e as Error).message, tone: 'alert' })), [toast]);
  useEffect(() => {
    load();
  }, [load]);
  return { info, load };
}

/** Back from Checkout: Stripe's webhook can take a few seconds, so check again until the subscription shows up. */
function useCheckoutReturn(onPaid: (p: Parent) => void) {
  const { toast } = useParent();
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const result = q.get('billing');
    if (!result) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (result !== 'success') return;
    toast({ text: 'Thank you! Your subscription is being set up…', tone: 'good' });
    let tries = 0;
    const t = window.setInterval(async () => {
      tries++;
      const p = await api.get<Parent>('/api/me').catch(() => null);
      if (p && (!p.billing?.locked || tries >= 10)) {
        window.clearInterval(t);
        onPaid(p);
      }
    }, 2000);
    return () => window.clearInterval(t);
  }, [onPaid, toast]);
}

/**
 * Settings → Plan & billing (#542): the family's plan in its own section at the top, with billing in the same card:
 * subscribe, change plan, or manage billing in Stripe's portal. Only the family owner pays.
 */
export function PlanSection() {
  const { parent } = useParent();
  const p = parent.plan;
  if (!p || p.status === 'unrestricted') return null;
  return (
    <>
      <h2 className="settings-group">Plan &amp; billing</h2>
      <YourPlan>
        {parent.billing?.enabled ? (
          <BillingControls />
        ) : (
          <p className="muted small">
            Want a different plan? Send us a note from <a href="/feedback">Feedback</a> and we’ll switch you.
          </p>
        )}
      </YourPlan>
    </>
  );
}

/** Billing inside Your plan: where the subscription stands, then Subscribe, or Change plan and Manage billing. */
function BillingControls() {
  const { setParent, toast } = useParent();
  const { info, load } = useBilling();
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const refresh = useCallback(
    (p?: Parent) => {
      (p ? Promise.resolve(p) : api.get<Parent>('/api/me')).then(setParent).catch(() => undefined);
      load();
    },
    [setParent, load],
  );
  useCheckoutReturn(refresh);
  if (!info) return null;
  async function manage() {
    setBusy(true);
    try {
      await go('/api/billing/portal');
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }
  const subscribed = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(info.status);
  const manageButton = (
    <button className="btn" onClick={manage} disabled={busy}>
      <ExternalLink size={16} /> {busy ? 'Opening…' : 'Manage billing'}
    </button>
  );
  return (
    <div className="stack sub-section billing-controls">
      <h3 className="with-icon">
        <CreditCard size={16} /> Billing
        {!!info.founderPercent && (
          <span className="founder-badge">
            <Star size={13} aria-hidden /> Founder · {info.founderPercent}% off for life
          </span>
        )}
      </h3>
      {!!info.founderPercent && (
        <p className="muted small">Thanks for joining early. Your Founder discount is applied at checkout and stays for as long as you subscribe, even if you change plans.</p>
      )}
      <p className={info.status === 'past_due' ? 'billing-warn' : ''}>{statusLine(info)}</p>
      {!info.isOwner ? (
        <p className="muted small">{info.ownerName || 'The family’s owner'} handles billing and plan changes for this family.</p>
      ) : !subscribed ? (
        <>
          <PlanPicker info={info} />
          {info.hasCustomer && <div>{manageButton}</div>}
        </>
      ) : (
        <>
          <div className="row-gap wrap">
            <button className={`btn ${changing ? 'active' : ''}`} onClick={() => setChanging((c) => !c)} aria-expanded={changing}>
              <ArrowUpDown size={16} /> Change plan
            </button>
            {manageButton}
          </div>
          <p className="muted small">Manage billing opens Stripe to change your card, see invoices, or cancel.</p>
          {changing && (
            <PlanPicker
              info={info}
              mode="change"
              onChanged={() => {
                setChanging(false);
                refresh();
              }}
            />
          )}
          {!!info.addons?.length && info.status !== 'past_due' && !info.pausedUntil && <Addons info={info} onChanged={() => refresh()} />}
          <SummerPause info={info} onChanged={() => refresh()} />
        </>
      )}
      {info.partner && !info.founderPercent && info.partner.discountPct > 0 && (
        <p className="small partner-note">
          🎁 From {info.partner.name}: {info.partner.discountPct}% off your first {info.partner.discountMonths === 1 ? 'month' : `${info.partner.discountMonths} months`}, applied
          when you subscribe.
        </p>
      )}
      {info.referral && <Referral r={info.referral} />}
    </div>
  );
}

/** Give a month, get a month (#554): the owner's link to share, and what it has earned. */
function Referral({ r }: { r: NonNullable<BillingInfo['referral']> }) {
  const { toast } = useParent();
  async function copy() {
    try {
      await navigator.clipboard.writeText(r.link);
      toast({ text: 'Link copied. Share it with a friend!', tone: 'good' });
    } catch {
      toast({ text: r.link, tone: 'good' });
    }
  }
  return (
    <div className="stack billing-referral">
      <h4 className="with-icon">
        <Gift size={16} /> Give a month, get a month
      </h4>
      <p className="muted small">Know a family who’d love ClassGuardian? When they sign up with your link and start paying, you both get a month free.</p>
      <div className="row-gap referral-link">
        <input type="text" readOnly value={r.link} aria-label="Your referral link" onFocus={(e) => e.target.select()} className="grow" />
        <button className="btn" onClick={copy}>
          <Copy size={16} /> Copy
        </button>
      </div>
      {r.signedUp > 0 && (
        <p className="small">
          {r.signedUp} famil{r.signedUp === 1 ? 'y' : 'ies'} joined with your link
          {r.rewarded ? ` · ${r.rewarded} earned you a free month` : ''}
          {r.creditCents ? ` (${money(r.creditCents)} in credit)` : ''}
          {r.pending ? ` · ${r.pending} waiting until you subscribe` : ''}.
        </p>
      )}
    </div>
  );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Pause for summer (#555): no bills for a few months, records stay open, computers aren't managed; or resume now. */
function SummerPause({ info, onChanged }: { info: BillingInfo; onChanged: () => void }) {
  const { toast } = useParent();
  const [months, setMonths] = useState(Math.min(3, info.canPauseMonths || 1));
  const [busy, setBusy] = useState(false);
  if (!info.pausedUntil && !info.canPauseMonths) return null;
  async function act(path: string, body: object | undefined, done: string, ask: string) {
    if (!confirm(ask)) return;
    setBusy(true);
    try {
      await api.post(path, body);
      toast({ text: done, tone: 'good' });
      onChanged();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
    setBusy(false);
  }
  if (info.pausedUntil) {
    return (
      <div className="billing-pause">
        <button className="btn" disabled={busy} onClick={() => act('/api/billing/resume', undefined, 'Welcome back! Billing and school time are back on.', 'Resume now? Billing starts again today and children’s computers are managed again.')}>
          <Play size={16} /> Resume now
        </button>
      </div>
    );
  }
  const until = new Date();
  until.setMonth(until.getMonth() + months);
  return (
    <div className="billing-pause stack">
      <h4 className="with-icon">
        <Pause size={16} /> Taking a summer break?
      </h4>
      <p className="muted small">Pause billing for up to {info.canPauseMonths} month{info.canPauseMonths === 1 ? '' : 's'}. Your records stay open; children’s computers aren’t managed until it resumes on its own.</p>
      <div className="row-gap wrap">
        <select value={months} onChange={(e) => setMonths(Number(e.target.value))} aria-label="How long">
          {Array.from({ length: info.canPauseMonths ?? 1 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m} month{m === 1 ? '' : 's'} (until {MONTH_NAMES[(new Date().getMonth() + m) % 12]})
            </option>
          ))}
        </select>
        <button
          className="btn"
          disabled={busy}
          onClick={() =>
            act('/api/billing/pause', { months }, `Paused until ${day(until.getTime())}.`, `Pause billing until ${day(until.getTime())}? No charges until then, and children’s computers aren’t managed. You can resume any time.`)
          }
        >
          <Pause size={16} /> Pause billing
        </button>
      </div>
    </div>
  );
}

/** Extras on top of the plan (#546): more children or adults, or features. Adding is charged now; removing is credited. */
function Addons({ info, onChanged }: { info: BillingInfo; onChanged: () => void }) {
  const { parent, toast } = useParent();
  const [busy, setBusy] = useState('');
  const yearly = info.interval === 'year';
  const label = (f: string) => parent.plan?.catalog.find((c) => c.key === f)?.label ?? f;
  async function set(a: NonNullable<BillingInfo['addons']>[number], quantity: number) {
    const each = yearly ? `${money(a.yearlyCents)}/year` : `${money(a.monthlyCents)}/month`;
    const ok =
      quantity > a.quantity
        ? confirm(`Add ${a.name}${a.kind === 'feature' ? '' : ` (${quantity} in all)`} for ${each}${a.kind === 'feature' ? '' : ' each'}? You’re charged today for the rest of this billing period.`)
        : confirm(`${quantity ? `Go down to ${quantity}` : `Remove ${a.name}`}? The unused part is credited to your next invoice.`);
    if (!ok) return;
    setBusy(a.id);
    try {
      const r = await api.post<{ chargedNow: boolean }>('/api/billing/addons', { addonId: a.id, quantity });
      toast({ text: r.chargedNow ? `${a.name} added. It was charged to your card.` : `${a.name} updated. The credit shows on your next invoice.`, tone: 'good' });
      onChanged();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
    setBusy('');
  }
  return (
    <div className="stack billing-addons">
      <h4 className="with-icon">
        <PackagePlus size={16} /> Add-ons
      </h4>
      {info.addons!.map((a) => (
        <div key={a.id} className="billing-addon">
          <div className="grow">
            <strong>{a.name}</strong>
            <div className="muted small">
              {a.kind === 'feature'
                ? a.features.map(label).join(', ')
                : `${a.amount} more ${a.kind === 'children' ? (a.amount === 1 ? 'child' : 'children') : a.amount === 1 ? 'adult' : 'adults'}`}{' '}
              · {yearly ? `${money(a.yearlyCents)}/year` : `${money(a.monthlyCents)}/month`}
              {a.kind !== 'feature' && ' each'}
            </div>
          </div>
          {a.kind === 'feature' ? (
            a.quantity ? (
              <button className="btn small" disabled={!!busy} onClick={() => set(a, 0)}>
                {busy === a.id ? 'Removing…' : 'Remove'}
              </button>
            ) : (
              <button className="btn small primary" disabled={!!busy} onClick={() => set(a, 1)}>
                <Plus size={14} /> {busy === a.id ? 'Adding…' : 'Add'}
              </button>
            )
          ) : (
            <div className="billing-qty" role="group" aria-label={`How many ${a.name}`}>
              <button className="icon-btn" disabled={!!busy || !a.quantity} onClick={() => set(a, a.quantity - 1)} aria-label={`One fewer ${a.name}`}>
                <Minus size={16} />
              </button>
              <span aria-live="polite">{a.quantity}</span>
              <button className="icon-btn" disabled={!!busy || a.quantity >= 20} onClick={() => set(a, a.quantity + 1)} aria-label={`One more ${a.name}`}>
                <Plus size={16} />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * What a locked family sees (#540): the trial ended, a payment failed past its grace, or the subscription ended.
 * They can subscribe or fix the card, download their data, or sign out. An admin can still open the Admin portal.
 */
export function BillingGate({ onUnlocked, onAdmin }: { onUnlocked: (p: Parent) => void; onAdmin?: () => void }) {
  const { parent, signOut, toast } = useParent();
  const { info } = useBilling();
  const [busy, setBusy] = useState(false);
  useCheckoutReturn(
    useCallback(
      (p: Parent) => {
        if (!p.billing?.locked) onUnlocked(p);
      },
      [onUnlocked],
    ),
  );
  async function manage() {
    setBusy(true);
    try {
      await go('/api/billing/portal');
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }
  const fixCard = !!info && ['past_due', 'unpaid', 'incomplete'].includes(info.status);
  // While this page shows, locked answers to background requests mustn't reload it (#638).
  useEffect(() => {
    setBillingPageShown(true);
    return () => setBillingPageShown(false);
  }, []);
  // A new family picks its plan (#635), and can go back to another family it belongs to meanwhile.
  const choosing = !!parent.billing?.needsPlan;
  const families = parent.families ?? [];
  const familyName = families.find((f) => f.id === parent.activeFamilyId)?.name ?? 'your family';
  const other = families.find((f) => f.id !== parent.activeFamilyId);
  async function switchTo(id: string) {
    try {
      onUnlocked(await api.put<Parent>('/api/me/active-family', { familyId: id }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  if (choosing)
    return (
      <main className="auth billing-gate">
        <div className="auth-card card stack wide-card">
          <Logo large />
          <h1>Choose a plan for {familyName}</h1>
          <p className="muted">
            Your new family is ready. Pick a plan to start{info?.plans.some((p) => p.trialDays) ? '; the free days start now, and you’re asked for a card only when they end' : ''}.
          </p>
          {!info ? (
            <p className="muted">Loading…</p>
          ) : !info.isOwner ? (
            <p>Ask {info.ownerName || 'the family’s owner'} to choose a plan.</p>
          ) : (
            <PlanPicker info={info} mode="choose" onChosen={onUnlocked} />
          )}
          <p className="muted center row-gap wrap billing-gate-links">
            {other && (
              <button type="button" className="link-btn" onClick={() => switchTo(other.id)}>
                ← Back to {other.name}
              </button>
            )}
            <a href="mailto:support@classguardian.app">Contact us</a>
            {onAdmin && (
              <button type="button" className="link-btn" onClick={onAdmin}>
                Open Admin
              </button>
            )}
            <button type="button" className="link-btn" onClick={signOut}>
              Sign out
            </button>
          </p>
        </div>
      </main>
    );
  return (
    <main className="auth billing-gate">
      <div className="auth-card card stack">
        <Logo large />
        <h1 className="with-icon">
          <Lock size={22} /> {fixCard ? 'Update your payment' : 'Subscribe to keep going'}
        </h1>
        <p className="muted">
          {parent.billing?.lockReason} Your family’s information is safe. {fixCard ? 'Update your card' : 'Subscribe'} and everything picks up where you left off.
          Until then, your children’s computers aren’t managed by ClassGuardian.
        </p>
        {!info ? (
          <p className="muted">Loading…</p>
        ) : !info.isOwner ? (
          <p>Ask {info.ownerName || 'the family’s owner'} to {fixCard ? 'update the card' : 'subscribe'} in Settings → Billing.</p>
        ) : fixCard ? (
          <button className="btn primary" onClick={manage} disabled={busy}>
            <CreditCard size={16} /> {busy ? 'Opening…' : 'Update payment'}
          </button>
        ) : (
          <PlanPicker info={info} />
        )}
        {info?.isOwner && info.hasCustomer && !fixCard && (
          <button className="link-btn" onClick={manage} disabled={busy}>
            See past invoices
          </button>
        )}
        {info?.isOwner && <DownloadRecords />}
        <ExportData />
        <p className="muted center row-gap wrap billing-gate-links">
          <a href="mailto:support@classguardian.app">Contact us</a>
          {onAdmin && (
            <button type="button" className="link-btn" onClick={onAdmin}>
              Open Admin
            </button>
          )}
          <button type="button" className="link-btn" onClick={signOut}>
            Sign out
          </button>
        </p>
      </div>
    </main>
  );
}

// --- Admin → Server settings → Payments ---------------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "May–Aug" for a run of months, else "Jan, Mar, Jun". */
function monthList(ms: number[]): string {
  const xs = [...new Set(ms)].sort((a, b) => a - b);
  if (!xs.length) return 'no months';
  const run = xs.every((m, i) => i === 0 || m === xs[i - 1]! + 1);
  return run && xs.length > 2 ? `${MONTHS[xs[0]! - 1]}–${MONTHS[xs[xs.length - 1]! - 1]}` : xs.map((m) => MONTHS[m - 1]).join(', ');
}

/** Connect Stripe with a secret key; switch billing on; grace days and Stripe Tax (#540). */
export function PaymentsSettings({ saved, onSaved }: { saved: PlatformSettings; onSaved: (s: PlatformSettings) => void }) {
  const { toast } = useParent();
  const b: BillingAdmin = saved.billing ?? { enabled: false, graceDays: 7, taxEnabled: true, connected: false, mode: '', accountName: '', accountId: '', webhookUrl: '', webhookSet: false, connectedAt: 0 };
  const [key, setKey] = useState('');
  const [grace, setGrace] = useState(b.graceDays);
  const [busy, setBusy] = useState(false);
  useEffect(() => setGrace(b.graceDays), [b.graceDays]);

  async function run(fn: () => Promise<unknown>, done: string) {
    setBusy(true);
    try {
      await fn();
      onSaved(await api.get<PlatformSettings>('/api/admin/settings'));
      toast({ text: done, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  const patch = (
    billing: Partial<
      Pick<BillingAdmin, 'enabled' | 'graceDays' | 'taxEnabled' | 'recordsKeepDays' | 'recordsAfterTrials' | 'checkoutConsent' | 'pauseEnabled' | 'pauseMaxMonths' | 'pauseMonths' | 'referralsEnabled' | 'referralMaxPerYear' | 'foundersEnabled' | 'foundersPercent' | 'newFamiliesChoosePlan'>
    >,
    done: string,
  ) => run(() => api.put('/api/admin/settings', { billing }), done);

  return (
    <div className="stack">
      <p className="muted small">
        Families pay for their plan (Admin → Plans) by card on Stripe’s secure pages. Use a <strong>test</strong> key (sk_test_…) on dev and a{' '}
        <strong>live</strong> key (sk_live_…) on the real server. Connecting sets up the webhook and the customer portal on your Stripe account for you.
      </p>
      <Fold title="Stripe connection" summary={b.connected ? `Connected · ${b.accountName} · ${b.mode === 'live' ? 'Live' : 'Test mode'}` : 'Not connected'}>
        {b.connected ? (
          <div className="billing-connected">
            <PlugZap size={18} aria-hidden />
            <div className="grow">
              <strong>{b.accountName}</strong> <span className={`tag ${b.mode === 'live' ? 'good' : 'warn'}`}>{b.mode === 'live' ? 'Live' : 'Test mode'}</span>
              <div className="muted small">
                Webhook {b.webhookSet ? '✓' : '✗'} {b.webhookUrl}
              </div>
              {/* Repair webhook (#587): match the endpoint in Stripe → Workbench → Webhooks, and see that events arrive. */}
              {b.webhookEndpointId && (
                <div className="muted small">
                  Endpoint ID <code>{b.webhookEndpointId}</code> · match it in Stripe → Workbench → Webhooks
                </div>
              )}
              <div className="muted small">
                Last event received: {b.lastEventAt ? `${new Date(b.lastEventAt).toLocaleString()} (${b.lastEventType})` : 'none yet'}
              </div>
              {!!b.lastBadSignatureAt && b.lastBadSignatureAt > (b.lastEventAt ?? 0) && (
                <div className="billing-warn small">
                  Stripe’s last event ({new Date(b.lastBadSignatureAt).toLocaleString()}) had a signature that didn’t match. The signing secret was probably rolled in Stripe: use Repair
                  webhook.
                </div>
              )}
            </div>
            <button className="btn danger-text" disabled={busy} onClick={() => confirm('Disconnect Stripe? Families can’t subscribe until you connect again. Existing subscriptions keep running in Stripe.') && run(() => api.post('/api/admin/billing/disconnect'), 'Stripe disconnected.')}>
              <Unplug size={16} /> Disconnect
            </button>
          </div>
        ) : null}
        {b.connected ? (
          <div className="row-gap billing-repair">
            <button
              className="btn"
              disabled={busy}
              onClick={() =>
                confirm(
                  'Repair the Stripe webhook? ClassGuardian makes a fresh endpoint in Stripe, saves its new secret, and deletes the other endpoints that point at this server. Families and subscriptions aren’t touched. Events sent while it was broken can be resent from Stripe.',
                ) &&
                run(() => api.post('/api/admin/billing/repair-webhook'), 'Webhook repaired. Stripe sends events to the new endpoint from now on.')
              }
            >
              <Wrench size={16} /> Repair webhook
            </button>
            <span className="muted small grow">Use this if you rolled the signing secret or deleted the endpoint in Stripe. It uses the saved key, so you don’t need it on hand.</span>
          </div>
        ) : (
          <label>
            Stripe secret key
            <div className="row-gap">
              <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk_test_…" className="grow" />
              <button className="btn primary" disabled={busy || !key.trim()} onClick={() => run(() => api.post('/api/admin/billing/connect', { secretKey: key.trim() }), 'Stripe connected.').then(() => setKey(''))}>
                {busy ? 'Connecting…' : 'Connect'}
              </button>
            </div>
            <span className="muted small block">In Stripe: Developers → API keys → Secret key. It’s stored encrypted and never shown again.</span>
          </label>
        )}
      </Fold>
      <label className="check">
        <input type="checkbox" checked={b.enabled} disabled={busy || (!b.connected && !b.enabled)} onChange={(e) => patch({ enabled: e.target.checked }, e.target.checked ? 'Billing is on.' : 'Billing is off.')} />
        <span>
          <strong>Billing is on</strong>
          <span className="muted small block">
            Families on a paid plan subscribe after any trial. Families with no plan, a free plan, free for life or in a free period aren’t affected. Off: nobody is asked to pay or
            locked.
          </span>
        </span>
      </label>
      <Fold title="Days of grace after a failed payment" summary={`${b.graceDays} day${b.graceDays === 1 ? '' : 's'}`}>
      <label>
        Days of grace
        <div className="row-gap">
          <input type="number" min={0} max={60} value={grace} onChange={(e) => setGrace(Number(e.target.value))} />
          {grace !== b.graceDays && (
            <button className="btn" disabled={busy} onClick={() => patch({ graceDays: grace }, 'Grace period saved.')}>
              Save
            </button>
          )}
        </div>
        <span className="muted small block">Stripe retries the card meanwhile and everything keeps working. After it, the family is locked until they update their card.</span>
      </label>
      </Fold>
      <label className="check">
        <input type="checkbox" checked={b.newFamiliesChoosePlan ?? true} disabled={busy} onChange={(e) => patch({ newFamiliesChoosePlan: e.target.checked }, 'Saved.')} />
        <span>
          <strong>New families choose a plan</strong>
          <span className="muted small block">
            A family started from Family and Sharing, or by signing up, picks a plan before it can be used, and its trial starts then. Off: new families get the
            default plan. Families that already exist aren’t affected.
          </span>
        </span>
      </label>
      <Fold
        title="Founders discount"
        summary={(b.foundersEnabled ?? true) ? `On · ${b.foundersPercent ?? 50}% off for life · ${b.founderFamilies ?? 0} famil${b.founderFamilies === 1 ? 'y' : 'ies'}` : 'Off'}
      >
      <label className="check">
        <input type="checkbox" checked={b.foundersEnabled ?? true} disabled={busy} onChange={(e) => patch({ foundersEnabled: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Offer a Founders discount</strong>
          <span className="muted small block">
            Families you mark as Founders (Admin → Families) get a lifetime discount on any plan, applied at checkout automatically. It stays through plan
            changes and renewals. {b.founderFamilies ?? 0} famil{b.founderFamilies === 1 ? 'y is' : 'ies are'} marked.
          </span>
        </span>
      </label>
      {(b.foundersEnabled ?? true) && (
        <label className="billing-pause-admin">
          Discount (% off, for life)
          <input type="number" min={1} max={100} defaultValue={b.foundersPercent ?? 50} onBlur={(e) => Number(e.target.value) !== b.foundersPercent && patch({ foundersPercent: Number(e.target.value) }, 'Saved. New Founders subscriptions get the new discount; ones already subscribed keep theirs.')} />
        </label>
      )}
      </Fold>
      <Fold title="Referrals" summary={(b.referralsEnabled ?? true) ? `On · up to ${b.referralMaxPerYear ?? 12} free months a year` : 'Off'}>
      <label className="check">
        <input type="checkbox" checked={b.referralsEnabled ?? true} disabled={busy} onChange={(e) => patch({ referralsEnabled: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Referrals: give a month, get a month</strong>
          <span className="muted small block">Family owners get a link to share. When a family that signed up with it first pays, both get a month of their plan as credit.</span>
        </span>
      </label>
      {(b.referralsEnabled ?? true) && (
        <label className="billing-pause-admin">
          Most free months one family can earn in a year
          <input type="number" min={0} max={100} defaultValue={b.referralMaxPerYear ?? 12} onBlur={(e) => Number(e.target.value) !== b.referralMaxPerYear && patch({ referralMaxPerYear: Number(e.target.value) }, 'Saved.')} />
        </label>
      )}
      </Fold>
      <Fold title="Pause for the summer" summary={(b.pauseEnabled ?? true) ? `On · up to ${b.pauseMaxMonths ?? 3} month${(b.pauseMaxMonths ?? 3) === 1 ? '' : 's'}, ${monthList(b.pauseMonths ?? [5, 6, 7, 8])}` : 'Off'}>
      <label className="check">
        <input type="checkbox" checked={b.pauseEnabled ?? true} disabled={busy} onChange={(e) => patch({ pauseEnabled: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Let families pause billing for the summer</strong>
          <span className="muted small block">No invoices while paused; their records stay open and their children’s computers aren’t managed. It resumes on its own.</span>
        </span>
      </label>
      {(b.pauseEnabled ?? true) && (
        <div className="stack billing-pause-admin">
          <label>
            Longest pause (months)
            <input type="number" min={1} max={12} defaultValue={b.pauseMaxMonths ?? 3} onBlur={(e) => Number(e.target.value) !== b.pauseMaxMonths && patch({ pauseMaxMonths: Number(e.target.value) }, 'Saved.')} />
          </label>
          <span className="muted small">A pause can start in:</span>
          <div className="chips" role="group" aria-label="Months a pause can start">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => {
              const on = (b.pauseMonths ?? [5, 6, 7, 8]).includes(i + 1);
              return (
                <button
                  key={m}
                  type="button"
                  className={`chip ${on ? 'active' : ''}`}
                  aria-pressed={on}
                  disabled={busy}
                  onClick={() => patch({ pauseMonths: on ? (b.pauseMonths ?? [5, 6, 7, 8]).filter((x) => x !== i + 1) : [...(b.pauseMonths ?? [5, 6, 7, 8]), i + 1] }, 'Saved.')}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}
      </Fold>
      <label>
        Keep a cancelled family’s records for
        <span className="row-gap">
          <input
            type="number"
            min={7}
            max={365}
            defaultValue={b.recordsKeepDays ?? 45}
            disabled={busy}
            onBlur={(e) => Number(e.target.value) !== (b.recordsKeepDays ?? 45) && patch({ recordsKeepDays: Math.min(365, Math.max(7, Number(e.target.value) || 45)) }, 'Saved.')}
            style={{ width: '6rem' }}
          />
          days after access ends, then delete them
        </span>
        <span className="muted small">They can download everything the whole time; a week before, they get a reminder (Billing notices → Records being deleted).</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={b.recordsAfterTrials ?? true} disabled={busy} onChange={(e) => patch({ recordsAfterTrials: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Also after a free trial ends without subscribing</strong>
          <span className="muted small block">The same days from the end of the trial.</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={b.taxEnabled} disabled={busy} onChange={(e) => patch({ taxEnabled: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Collect sales tax with Stripe Tax</strong>
          <span className="muted small block">Stripe works out and adds sales tax at checkout. Set up your tax registrations in Stripe → Settings → Tax first.</span>
        </span>
      </label>
      <label className="check">
        <input type="checkbox" checked={b.checkoutConsent ?? true} disabled={busy} onChange={(e) => patch({ checkoutConsent: e.target.checked }, 'Saved.')} />
        <span>
          <strong>Ask families to agree to the renewal terms at checkout</strong>
          <span className="muted small block">
            A box on Stripe’s checkout page they tick before paying: the Terms of Service, and that their plan renews automatically at its price until they cancel.
            Several state laws require it. When they agreed, and the words, are kept on the family. Needs your Terms address in Stripe → Settings → Public details.
          </span>
        </span>
      </label>
    </div>
  );
}
